import axios from 'axios';
import { getUrl } from '../service-utils';
import authHeader from '../auth-header';
import { ITripActivity } from '../../Models/trips/trip-activity.model';
import { IAddActivityModel } from '../../components/modals/activity/use-add-activity';
import { IEditActivityModel } from '../../components/modals/activity/use-edit-activity';

// Response from backend when creating an activity
export interface ICreateActivityResponse {
  trip: {
    id: string;
    title: string;
    destinationCount: number;
  };
  activity: ITripActivity;
  folder: {
    id: string;
    title: string;
    type: string;
  } | null;
  message: string;
}

// Extended response for createActivityFromPlace — may return selection candidates
export interface ICreateActivityFromPlaceResponse {
  status: 'created' | 'reused' | 'alreadyLinked' | 'selectionRequired';
  trip?: {
    id: string;
    title: string;
    destinationCount: number;
  };
  activity?: ITripActivity;
  folder?: { id: string; title: string; type: string } | null;
  candidates?: Array<{ id: string; title: string }>;
  message: string;
}

export interface ICreateActivityFromPlaceRequest {
  internalPlaceId?: string;
  googlePlaceId?: string;
  /**
   * @deprecated Use internalPlaceId or googlePlaceId instead.
   * Kept only for the migration window.
   */
  placeId?: string;
  researchId?: string;
  startTime?: number;
  placeData?: {
    name?: string;
    latitude?: number;
    longitude?: number;
    types?: string[];
    addressComponents?: any[];
  };
}

export type DiscoveryActivitySourceType =
  | 'PLACE'
  | 'YOUTUBE'
  | 'WEBPAGE'
  | 'AI'
  | 'IMAGE';

export interface ICreateActivityFromWebpageRequest {
  researchId: string;
  url?: string;
  snippet?: string;
  imageUrl?: string;
}

export interface ICreateActivityFromAIRequest {
  researchId: string;
  prompt?: string;
  snippet?: string;
  imageUrl?: string;
}

export interface ICreateActivityFromImageRequest {
  researchId: string;
  imageUrl?: string;
  snippet?: string;
}

export interface IAttachResearchToActivityRequest {
  researchId: string;
  sourceType?: DiscoveryActivitySourceType;
  startTime?: number;
}

class TripActivityService {
  private API_URL: string;

  constructor() {
    this.API_URL = getUrl();
  }

  public async getActivities(tripId: string) {
    try {
      const response = await axios.get(
        `${this.API_URL}trips/${tripId}/activities`,
        { headers: authHeader() }
      );
      return response.data.data as ITripActivity[];
    } catch (error: any) {
      console.error(
        'Error fetching activities:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async getActivityById(
    tripId: string,
    activityId: string,
    destinationId?: string
  ) {
    try {
      const url = destinationId
        ? `${this.API_URL}trips/${tripId}/destinations/${destinationId}/activities/${activityId}`
        : `${this.API_URL}trips/${tripId}/activities/${activityId}`;

      const response = await axios.get(url, { headers: authHeader() });
      return response.data.data as ITripActivity;
    } catch (error: any) {
      console.error(
        'Error fetching activity:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivity(
    tripId: string,
    activity: IAddActivityModel
  ): Promise<ICreateActivityResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities`,
        activity,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivityFromPlace(
    tripId: string,
    request: ICreateActivityFromPlaceRequest
  ): Promise<ICreateActivityFromPlaceResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/createfromplace`,
        request,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityFromPlaceResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity from place:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivityFromYouTube(
    tripId: string,
    researchId: string,
    startTime?: number
  ): Promise<ICreateActivityFromPlaceResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/createfromyoutube`,
        { researchId, startTime },
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityFromPlaceResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity from YouTube research:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivityFromWebpage(
    tripId: string,
    request: ICreateActivityFromWebpageRequest
  ): Promise<ICreateActivityResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/createfromwebpage`,
        request,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity from webpage research:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivityFromAI(
    tripId: string,
    request: ICreateActivityFromAIRequest
  ): Promise<ICreateActivityResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/createfromai`,
        request,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity from AI research:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async createActivityFromImage(
    tripId: string,
    request: ICreateActivityFromImageRequest
  ): Promise<ICreateActivityResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/createfromimage`,
        request,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityResponse;
    } catch (error: any) {
      console.error(
        'Error creating activity from image research:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async attachResearchToActivity(
    tripId: string,
    activityId: string,
    request: IAttachResearchToActivityRequest
  ): Promise<ICreateActivityFromPlaceResponse> {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/${activityId}/attach-research-from-source`,
        request,
        { headers: authHeader() }
      );
      return response.data.data as ICreateActivityFromPlaceResponse;
    } catch (error: any) {
      console.error(
        'Error attaching research to activity:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async attachResearchFromPlace(
    tripId: string,
    activityId: string,
    researchId: string,
    startTime?: number
  ): Promise<ICreateActivityFromPlaceResponse> {
    return this.attachResearchToActivity(tripId, activityId, {
      researchId,
      sourceType: 'PLACE',
      startTime
    });
  }

  public async updateActivity(
    tripId: string,
    activityId: string,
    activity: IEditActivityModel,
    destinationId?: string
  ) {
    try {
      const url = destinationId
        ? `${this.API_URL}trips/${tripId}/destinations/${destinationId}/activities/${activityId}`
        : `${this.API_URL}trips/${tripId}/activities/${activityId}`;

      const method = destinationId ? axios.patch : axios.put;

      const response = await method(url, activity, { headers: authHeader() });
      return response.data.data as ITripActivity;
    } catch (error: any) {
      console.error(
        'Error updating activity:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async deleteActivity(tripId: string, activityId: string) {
    try {
      const response = await axios.delete(
        `${this.API_URL}trips/${tripId}/activities/${activityId}`,
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error deleting activity:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async moveToFolder(
    tripId: string,
    activityId: string,
    sourceFolderId: string,
    destinationFolderId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/${activityId}/move`,
        {
          sourceFolderId,
          destinationFolderId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving activity to folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  public async copyToFolder(
    tripId: string,
    activityId: string,
    sourceFolderId: string,
    destinationFolderId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/${activityId}/copy`,
        {
          sourceFolderId,
          destinationFolderId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying activity to folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy activities from one trip to another trip
   */
  public async copyActivitiesToTrip(
    sourceTripId: string,
    targetTripId: string,
    activityIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/activities/copy`,
        {
          sourceTripId,
          targetTripId,
          activityIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying activities to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move activities from one trip to another trip
   */
  public async moveActivitiesToTrip(
    sourceTripId: string,
    targetTripId: string,
    activityIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${targetTripId}/activities/move`,
        {
          sourceTripId,
          targetTripId,
          activityIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving activities to trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy activities within the same trip between destinations
   */
  public async copyActivitiesWithinTrip(
    tripId: string,
    sourceDestinationId: string,
    targetDestinationId: string,
    activityIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/copy-within`,
        {
          tripId,
          sourceDestinationId,
          targetDestinationId,
          activityIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying activities within trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move activities within the same trip between destinations
   */
  public async moveActivitiesWithinTrip(
    tripId: string,
    sourceDestinationId: string,
    targetDestinationId: string,
    activityIds: string[]
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/activities/move-within`,
        {
          tripId,
          sourceDestinationId,
          targetDestinationId,
          activityIds
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving activities within trip:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Move activity to a different folder within the same destination
   */
  public async moveActivityToFolder(
    tripId: string,
    destinationId: string,
    activityId: string,
    sourceFolderId: string,
    targetFolderId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations/${destinationId}/activities/move-to-folder`,
        {
          activityId,
          sourceFolderId,
          targetFolderId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error moving activity to folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Copy activity to a different folder within the same destination
   */
  public async copyActivityToFolder(
    tripId: string,
    destinationId: string,
    activityId: string,
    sourceFolderId: string,
    targetFolderId: string
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}trips/${tripId}/destinations/${destinationId}/activities/copy-to-folder`,
        {
          activityId,
          sourceFolderId,
          targetFolderId
        },
        { headers: authHeader() }
      );
      return response.data;
    } catch (error: any) {
      console.error(
        'Error copying activity to folder:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  // ===== VIATOR TOUR/ACTIVITY INTEGRATION =====

  /**
   * Search for tours and activities by place ID
   * @param placeId - The ID of the place in the database
   * @param searchString - Optional search query to refine results
   * @param limit - Maximum number of results (default 6)
   */
  public async searchToursByPlaceId(
    placeId: string,
    searchString?: string,
    limit: number = 6
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}places/${placeId}/tours`,
        { searchString, limit },
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error searching tours by place ID:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Search for tours and activities by GPS coordinates
   * @param latitude - Latitude coordinate
   * @param longitude - Longitude coordinate
   * @param searchString - Optional search query to refine results
   * @param limit - Maximum number of results (default 6)
   */
  public async searchToursByCoordinates(
    latitude: number,
    longitude: number,
    searchString?: string,
    limit: number = 6
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}places/tours/coordinates`,
        { latitude, longitude, searchString, limit },
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error searching tours by coordinates:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  /**
   * Search for tours and activities by place name and parent place
   * @param placeName - Name of the place (e.g., "Eiffel Tower")
   * @param parentPlaceName - Parent place name for context (e.g., "Paris")
   * @param searchString - Optional search query to refine results
   * @param limit - Maximum number of results (default 6)
   */
  public async searchToursByPlaceName(
    placeName: string,
    parentPlaceName: string,
    searchString?: string,
    limit: number = 6
  ) {
    try {
      const response = await axios.post(
        `${this.API_URL}places/tours/search`,
        { placeName, parentPlaceName, searchString, limit },
        { headers: authHeader() }
      );
      return response.data.data;
    } catch (error: any) {
      console.error(
        'Error searching tours by place name:',
        error?.response?.data || error.message
      );
      throw error?.response?.data || error;
    }
  }

  // public convertActivitiesToSummary(
  //   trip: ITrip,
  //   folderActivityIds: string[]
  // ): ITripActivitySummary[] {
  //   return trip.activities
  //     .filter((act) => folderActivityIds.includes(act.id ?? ''))
  //     .map((act) => ({
  //       id: act.id ?? '',
  //       title: act.title,
  //       description: act.description,
  //       tripId: act.tripId ?? trip.id,
  //       imageUrl: act.imageUrl,
  //       duration: act.notes ? act.notes.join(', ') : undefined,
  //       lat: act.latitude,
  //       long: act.longitude
  //     }));
  // }
}

const tripActivityService = new TripActivityService();
export default tripActivityService;
